import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Link2, Loader2 } from 'lucide-react';
import { Modal } from '../../../reusable';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

const scoreColor = (score) => {
  if (score >= 90) return '#16a34a';
  if (score >= 60) return '#d97706';
  return '#dc2626';
};

const CHECK_LABELS = {
  folio: { match: 'Coincide', miss: 'Distinto' },
  rut: { match: 'Coincide', miss: 'Distinto' },
  amount: { exact: 'Exacto (neto o c/IVA)', near: 'Cercano (±1%)', miss: 'Distinto' },
  date: { match: 'Dentro de 30 días', miss: 'Más de 30 días' },
};

const checkTone = (value) => {
  if (value === 'match' || value === 'exact') return '#16a34a';
  if (value === 'near') return '#d97706';
  if (value === 'miss') return '#dc2626';
  return '#94a3b8';
};

const RESULT_PRESENTATION = {
  linked: { label: 'Vinculada', color: '#16a34a' },
  skipped: { label: 'Omitida', color: '#64748b' },
};

/**
 * Conciliación del libro de ventas: a la izquierda el listado de documentos con venta sugerida
 * (match por folio ingresado en el módulo de Ventas) y a la derecha la comparación con la venta.
 */
const AccountingSalesReconcileModal = ({ isOpen = false, onClose, rows = [], onLink, isDarkMode = false, ui }) => {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState({});
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const pending = rows
      .filter((row) => row.has_pending_sale_match && Number(row.suggested_sale_id) > 0)
      .sort(
        (a, b) =>
          String(a.doc_date || '').localeCompare(String(b.doc_date || '')) || Number(a.id) - Number(b.id)
      );
    setQueue(pending);
    setIndex(0);
    setResults({});
    setError('');
    // Solo al abrir: la cola queda fija mientras se concilia aunque el libro se recargue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-row-index="${index}"]`);
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [index]);

  const counts = useMemo(() => {
    const values = Object.values(results);
    return {
      linked: values.filter((value) => value === 'linked').length,
      skipped: values.filter((value) => value === 'skipped').length,
    };
  }, [results]);

  const text = ui?.text || (isDarkMode ? '#f4f4f5' : '#111827');
  const muted = ui?.muted || (isDarkMode ? '#a1a1aa' : '#6b7280');
  const border = ui?.border || (isDarkMode ? '#3f3f46' : '#e5e7eb');
  const headerBg = ui?.header || (isDarkMode ? '#18181b' : '#f4f4f5');
  const selectedBg = isDarkMode ? 'rgba(37, 99, 235, 0.18)' : '#eff6ff';
  const total = queue.length;
  const reviewed = Object.keys(results).length;
  const allReviewed = total > 0 && reviewed >= total;
  const current = queue[index] || null;
  const currentResult = current ? results[current.id] : null;

  const nextPendingIndex = (fromIndex, nextResults) => {
    for (let step = 1; step <= total; step += 1) {
      const candidate = (fromIndex + step) % total;
      if (!nextResults[queue[candidate].id]) return candidate;
    }
    return fromIndex;
  };

  const handleSkip = () => {
    if (!current) return;
    setError('');
    if (currentResult) {
      setIndex(Math.min(total - 1, index + 1));
      return;
    }
    const nextResults = { ...results, [current.id]: 'skipped' };
    setResults(nextResults);
    setIndex(nextPendingIndex(index, nextResults));
  };

  const handleLink = async () => {
    if (!current || currentResult === 'linked') return;
    try {
      setIsLinking(true);
      setError('');
      await onLink?.(current);
      const nextResults = { ...results, [current.id]: 'linked' };
      setResults(nextResults);
      setIndex(nextPendingIndex(index, nextResults));
    } catch (err) {
      setError(err?.message || 'No se pudo vincular la factura.');
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    if (isLinking) return;
    onClose?.({ linked: counts.linked });
  };

  const btn = (variant, enabled = true) => {
    const filled = variant === 'success';
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 14px',
      borderRadius: 8,
      border: filled ? 'none' : `1px solid ${ui?.inputBorder || border}`,
      background: filled ? '#16a34a' : ui?.inputBg || 'transparent',
      color: filled ? '#fff' : text,
      fontWeight: 600,
      fontSize: 13,
      cursor: enabled ? 'pointer' : 'not-allowed',
      opacity: enabled ? 1 : 0.5,
    };
  };

  const box = {
    border: `1px solid ${border}`,
    borderRadius: 10,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const boxHeader = {
    padding: '10px 12px',
    borderBottom: `1px solid ${border}`,
    background: headerBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };
  const th = (align = 'left') => ({
    position: 'sticky',
    top: 0,
    background: headerBg,
    textAlign: align,
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: muted,
    borderBottom: `1px solid ${border}`,
    whiteSpace: 'nowrap',
    zIndex: 1,
  });
  const td = (align = 'left') => ({
    textAlign: align,
    padding: '8px 10px',
    fontSize: 12,
    color: text,
    borderTop: `1px solid ${border}`,
    verticalAlign: 'top',
  });

  const renderList = () => (
    <section style={box}>
      <div style={boxHeader}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: text }}>Libro de ventas</h4>
        <span style={{ fontSize: 12, color: muted }}>
          {reviewed} de {total} revisada(s)
        </span>
      </div>
      <div ref={listRef} style={{ overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th()}>Documento</th>
              <th style={th()}>Cliente</th>
              <th style={th('right')}>Total</th>
              <th style={th()}>Sugerida</th>
              <th style={th('right')}>%</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row, rowIndex) => {
              const isSelected = rowIndex === index;
              const result = RESULT_PRESENTATION[results[row.id]];
              const score = Math.round(Number(row.suggested_match_score || 0));
              return (
                <tr
                  key={row.id}
                  data-row-index={rowIndex}
                  onClick={() => {
                    if (isLinking) return;
                    setError('');
                    setIndex(rowIndex);
                  }}
                  style={{
                    cursor: isLinking ? 'wait' : 'pointer',
                    background: isSelected ? selectedBg : 'transparent',
                    boxShadow: isSelected ? 'inset 3px 0 0 #2563eb' : 'none',
                    opacity: result && !isSelected ? 0.6 : 1,
                  }}
                >
                  <td style={td()}>
                    <div style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {row.document_type ? `${row.document_type} ` : ''}
                      {row.doc_number || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: muted }}>{formatDate(row.doc_date)}</div>
                  </td>
                  <td style={{ ...td(), maxWidth: 180 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.counterpart_name || '—'}
                    </div>
                    {result ? (
                      <div style={{ fontSize: 11, fontWeight: 700, color: result.color }}>{result.label}</div>
                    ) : null}
                  </td>
                  <td style={{ ...td('right'), whiteSpace: 'nowrap' }}>{money(row.total_amount)}</td>
                  <td style={{ ...td(), fontWeight: 600, whiteSpace: 'nowrap' }}>{row.suggested_sale_code || '—'}</td>
                  <td style={{ ...td('right'), fontWeight: 700, color: scoreColor(score) }}>{score}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderComparison = () => {
    if (!current) return null;
    const sale = current.suggested_sale || {};
    const matched = sale.matched_document || null;
    const isManual = matched?.source === 'manual';
    const checks = current.suggested_match_checks || {};
    const score = Math.round(Number(current.suggested_match_score || 0));
    const saleNet = Number(matched?.net_amount || sale.total_amount || 0);
    const saleTotal = Number(matched?.total_amount || 0) || saleNet * 1.19;
    const result = RESULT_PRESENTATION[currentResult];
    const sign = Number(current.total_amount || 0) < 0 ? -1 : 1;
    const check = (key) => {
      const value = checks[key] || 'na';
      return (
        <span style={{ fontWeight: 700, color: checkTone(value), whiteSpace: 'nowrap' }}>
          {CHECK_LABELS[key][value] || 'Sin dato'}
        </span>
      );
    };
    const comparisonRows = [
      {
        label: 'Folio',
        doc: current.doc_number,
        sale: matched?.doc_number || sale.invoice_number,
        result: check('folio'),
      },
      {
        label: 'Tipo',
        doc: current.document_type,
        sale: matched?.document_type || 'Factura de la venta',
        result: null,
      },
      {
        label: 'Origen',
        doc: 'Libro de ventas SII',
        sale: isManual ? 'Ingreso manual · pendiente de conciliación' : 'Factura principal de la venta',
        result: null,
      },
      {
        label: 'Fecha',
        doc: formatDate(current.doc_date),
        sale: formatDate(matched?.doc_date || sale.invoice_date || sale.deal_close_date),
        result: check('date'),
      },
      { label: 'Cliente', doc: current.counterpart_name, sale: sale.organization_name, result: null },
      { label: 'RUT', doc: current.counterpart_rut, sale: sale.organization_rut, result: check('rut') },
      { label: 'Neto', doc: money(current.net_amount), sale: saleNet ? money(sign * saleNet) : '', result: null },
      {
        label: 'IVA',
        doc: money(current.tax_amount),
        sale: saleNet ? money(sign * (saleTotal - saleNet)) : '',
        result: null,
      },
      {
        label: 'Total',
        doc: money(current.total_amount),
        sale: saleNet ? money(sign * saleTotal) : '',
        result: check('amount'),
        strong: true,
      },
    ];
    return (
      <section style={box}>
        <div style={boxHeader}>
          <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: text }}>
              {`${current.document_type || 'Documento'} ${current.doc_number || ''}`.trim()} ↔{' '}
              {current.suggested_sale_code || sale.sale_code || 'Venta'}
            </h4>
            {result ? (
              <span style={{ fontSize: 12, fontWeight: 700, color: result.color }}>{result.label}</span>
            ) : (
              <span style={{ fontSize: 12, color: muted }}>Pendiente de revisión</span>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: muted }}>Coincidencia</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(score), lineHeight: 1.1 }}>{score}%</div>
          </div>
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th()}>Campo</th>
                <th style={th()}>Documento tributario (SII)</th>
                <th style={th()}>Documento en la venta sugerida</th>
                <th style={th()}>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label}>
                  <td style={{ ...td(), color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</td>
                  <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.doc || '—'}</td>
                  <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.sale || '—'}</td>
                  <td style={td()}>{row.result || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isManual ? (
            <p style={{ margin: 0, padding: '8px 10px', fontSize: 11, color: muted }}>
              La venta guarda el monto neto; IVA y total de la venta son estimados (19%).
            </p>
          ) : null}
        </div>
      </section>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Conciliar libro de ventas"
      subtitle="Facturas del SII con venta sugerida según el folio ingresado en el módulo de Ventas."
      size="xxl"
      isDarkMode={isDarkMode}
      closeOnOverlayClick={!isLinking}
      closeOnEscape={!isLinking}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ fontSize: 12, color: allReviewed ? '#16a34a' : muted, fontWeight: allReviewed ? 700 : 400 }}>
            {allReviewed ? 'Conciliación terminada · ' : ''}
            {counts.linked} vinculada(s) · {counts.skipped} omitida(s) · {total - reviewed} pendiente(s)
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {current ? (
              <>
                <button
                  type="button"
                  onClick={() => setIndex((value) => Math.max(0, value - 1))}
                  disabled={isLinking || index === 0}
                  style={btn('ghost', !isLinking && index > 0)}
                >
                  <ChevronLeft size={16} /> Anterior
                </button>
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isLinking || (currentResult && index >= total - 1)}
                  style={btn('ghost', !isLinking && !(currentResult && index >= total - 1))}
                >
                  {currentResult ? 'Siguiente' : 'Omitir'} <ChevronRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={handleLink}
                  disabled={isLinking || currentResult === 'linked'}
                  style={btn('success', !isLinking && currentResult !== 'linked')}
                >
                  {isLinking ? <Loader2 size={16} /> : currentResult === 'linked' ? <Check size={16} /> : <Link2 size={16} />}
                  {isLinking
                    ? 'Vinculando…'
                    : currentResult === 'linked'
                    ? 'Vinculada'
                    : `Vincular con ${current.suggested_sale_code || 'venta'}`}
                </button>
              </>
            ) : null}
            <button type="button" onClick={handleClose} disabled={isLinking} style={btn('ghost', !isLinking)}>
              Cerrar
            </button>
          </div>
        </div>
      }
    >
      {total ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ height: 6, borderRadius: 999, background: border, overflow: 'hidden' }}>
            <div
              style={{
                width: `${(reviewed / total) * 100}%`,
                height: '100%',
                background: '#16a34a',
                transition: 'width 0.2s',
              }}
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)',
              gap: 12,
              height: '60vh',
              minHeight: 360,
            }}
          >
            {renderList()}
            {renderComparison()}
          </div>
          {error ? <p style={{ margin: 0, fontSize: 13, color: ui?.danger || '#dc2626' }}>{error}</p> : null}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: muted, padding: '24px 0', textAlign: 'center' }}>
          No hay facturas con venta sugerida pendiente en el período seleccionado.
        </p>
      )}
    </Modal>
  );
};

export default AccountingSalesReconcileModal;
